var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
if (!customElements.get("ona-video-slot")) {
  class OnaVideoSlot extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: "open" });
      this.video = document.createElement("video");
      this.video.style.opacity = "0";
      this.video.style.transition = "opacity var(--ona-video-slot-transition-duration, 300ms) ease";
      this.video.playsInline = true;
      this._onLoadedData = () => this._onVideoLoaded();
      this._onError = () => this._onVideoError();
      this.video.addEventListener("loadeddata", this._onLoadedData);
      this.video.addEventListener("error", this._onError);
      const style = document.createElement("style");
      style.textContent = `
        :host {
          position: relative;
          display: block;
          width: 100%;
          aspect-ratio: var(--ona-video-slot-aspect-ratio, 16 / 9);
          background: var(--ona-video-slot-background-color, white);
          overflow: hidden;
          border-radius: var(--ona-video-slot-border-radius, inherit);
        }

        :host([background]) {
          position: absolute;
          inset: 0;
          z-index: var(--ona-video-slot-background-zindex, -1);
          aspect-ratio: auto;
        }

        video {
          position: relative;
          z-index: 2;
          width: 100%;
          height: 100%;
          object-fit: var(--ona-video-slot-object-fit, cover);
          display: block;
          border-radius: inherit;
          transition: opacity var(--ona-video-slot-transition-duration, 300ms) ease;
          background: transparent;
        }

        .loader,
        .fallback {
          position: absolute;
          inset: 0;
          display: grid;
          place-content: center;
          background: inherit;
          z-index: 1;
          pointer-events: none;
        }

        ::slotted([slot="loader"]),
        ::slotted([slot="fallback"]) {
          width: 100%;
          height: 100%;
          display: grid;
          place-content: center;
        }

        .default-error {
          font-size: 0.85em;
          color: var(--ona-video-slot-error-color, #900);
          text-align: center;
        }

        @media (prefers-reduced-motion: reduce) {
          video { transition: none !important; }
        }

        :host([debug]) {
          outline: 1px dashed var(--ona-video-slot-debug-outline, red);
        }

        .debug-panel {
          position: absolute;
          bottom: 0;
          left: 0;
          font-family: monospace;
          font-size: 10px;
          background: var(--ona-video-slot-debug-bg, rgba(255,255,255,0.9));
          color: var(--ona-video-slot-debug-color, #000);
          padding: 2px 4px;
          z-index: 10;
          pointer-events: none;
          white-space: pre;
          border-top-right-radius: 4px;
        }
      `;
      this.loader = document.createElement("div");
      this.loader.className = "loader";
      this.loader.innerHTML = `
        <slot name="loader">
          <ona-spinner></ona-spinner>
        </slot>
      `;
      this.fallback = document.createElement("div");
      this.fallback.className = "fallback";
      this.fallback.style.display = "none";
      this.fallback.innerHTML = `
        <slot name="fallback"></slot>
      `;
      this.debugPanel = document.createElement("div");
      this.debugPanel.className = "debug-panel";
      this.debugPanel.style.display = "none";
      this.shadowRoot.append(style, this.video, this.loader, this.fallback, this.debugPanel);
      this._timeout = null;
      this._resolveLoad = null;
      this._rejectLoad = null;
    }
    connectedCallback() {
      if (window.__ONA_DEBUG__ && !this.hasAttribute("debug")) {
        this.setAttribute("debug", "");
      }
      this._updateAttributes();
      this._updateDebug();
      this._timeout = setTimeout(() => {
        if (!this.video.readyState || this.video.readyState < 2) {
          this.loader.style.display = "grid";
        }
      }, 500);
      this._dispatch("loadstart");
    }
    attributeChangedCallback(name) {
      if (["debug", "background"].includes(name)) {
        this._updateDebug();
      }
      this._updateAttributes();
    }
    _updateAttributes() {
      while (this.video.firstChild) {
        this.video.removeChild(this.video.firstChild);
      }
      const srcset = this.getAttribute("srcset");
      const sizes = this.getAttribute("sizes");
      let selectedSrc = "";
      let selectedType = "";
      if (srcset) {
        const candidates = srcset.split(",").map((item) => {
          const [url, w] = item.trim().split(/\s+/);
          return {
            src: url,
            width: w && w.endsWith("w") ? parseInt(w) : null,
            type: url.split(".").pop()
          };
        }).filter((c) => c.src);
        let slotWidth = this.offsetWidth || 0;
        if (!slotWidth && this.parentElement) {
          slotWidth = this.parentElement.offsetWidth;
        }
        let targetWidth = slotWidth;
        if (sizes) {
          const match = sizes.match(/(\d+)(vw|px)/);
          if (match) {
            if (match[2] === "vw") {
              targetWidth = window.innerWidth * (parseInt(match[1]) / 100);
            } else if (match[2] === "px") {
              targetWidth = parseInt(match[1]);
            }
          }
        }
        targetWidth = targetWidth * (window.devicePixelRatio || 1);
        let best = candidates.filter((c) => c.width).sort((a, b) => a.width - b.width).find((c) => c.width >= targetWidth);
        if (!best) {
          best = candidates.filter((c) => c.width).sort((a, b) => b.width - a.width)[0];
        }
        if (!best && candidates.length) best = candidates[0];
        if (best) {
          selectedSrc = best.src;
          if (best.type === "webm") selectedType = "video/webm";
          else if (best.type === "mp4") selectedType = "video/mp4";
          else if (best.type === "ogg" || best.type === "ogv") selectedType = "video/ogg";
        }
      }
      if (selectedSrc) {
        const el = document.createElement("source");
        el.src = selectedSrc;
        if (selectedType) el.type = selectedType;
        this.video.appendChild(el);
      } else if (this.getAttribute("src")) {
        const el = document.createElement("source");
        el.src = this.getAttribute("src");
        this.video.appendChild(el);
      }
      this.video.poster = this.getAttribute("poster") || "";
      this.video.autoplay = this.hasAttribute("autoplay");
      this.video.muted = this.hasAttribute("muted");
      this.video.loop = this.hasAttribute("loop");
      this.video.controls = this.hasAttribute("controls");
    }
    _updateDebug() {
      const isDebug = this.hasAttribute("debug");
      this.debugPanel.style.display = isDebug ? "block" : "none";
      if (isDebug) {
        let state = "loading";
        if (this.video.readyState >= 2) {
          state = "loaded";
        } else if (this.fallback.style.display !== "none") {
          state = "error";
        }
        this.debugPanel.textContent = `ona-video-slot (debug)
status: ${state}
src: ${this.getAttribute("src") || ""}
srcset: ${this.getAttribute("srcset") || ""}
sizes: ${this.getAttribute("sizes") || ""}
poster: ${this.getAttribute("poster") || ""}`;
      }
    }
    _onVideoLoaded() {
      clearTimeout(this._timeout);
      this.video.style.opacity = "1";
      this.loader.style.display = "none";
      this.fallback.style.display = "none";
      this._updateDebug();
      this._dispatch("loadsuccess", { video: this.video });
      if (this._resolveLoad) this._resolveLoad(this.video);
      this._resolveLoad = this._rejectLoad = null;
    }
    _onVideoError() {
      clearTimeout(this._timeout);
      this.video.style.opacity = "0.3";
      this.loader.style.display = "none";
      this.fallback.style.display = "grid";
      this._updateDebug();
      this._dispatch("loaderror", { error: new Error("Video failed to load") });
      if (this._rejectLoad) this._rejectLoad(new Error("Video failed to load"));
      this._resolveLoad = this._rejectLoad = null;
    }
    _dispatch(name, detail = {}) {
      this.dispatchEvent(
        new CustomEvent(name, {
          detail,
          bubbles: true,
          composed: true
        })
      );
    }
    load() {
      return new Promise((resolve, reject) => {
        if (this.video.readyState >= 2) {
          resolve(this.video);
        } else {
          this._resolveLoad = resolve;
          this._rejectLoad = reject;
        }
      });
    }
    play() {
      return this.video.play();
    }
    pause() {
      this.video.pause();
    }
    stop() {
      this.video.pause();
      this.video.currentTime = 0;
    }
  }
  __publicField(OnaVideoSlot, "observedAttributes", [
    "src",
    "srcset",
    "sizes",
    "poster",
    "autoplay",
    "muted",
    "loop",
    "controls",
    "debug",
    "background"
  ]);
  customElements.define("ona-video-slot", OnaVideoSlot);
}
